import { useState, useRef, useEffect } from 'react'
import { spritesheetApi } from '../services/spritesheetApi'
import { useCritterStore } from '../store/critterStore'
import { ANIMATION_STATES } from '../constants/animationStates'
import '../styles/UploadSpritesheetPanel.css'

const ANIMATION_STATE_LABELS = {
  idle: 'Idle',
  walk: 'Walking',
  eat: 'Eating',
  pet: 'Being Petted'
}

const UploadSpritesheetPanel = ({ isOpen, onClose, broadcasterLogin }) => {
  const [selectedCritterType, setSelectedCritterType] = useState('')
  const [selectedAnimationState, setSelectedAnimationState] = useState('idle')
  const [pngFile, setPngFile] = useState(null)
  const [jsonFile, setJsonFile] = useState(null)
  const [pngPreview, setPngPreview] = useState(null)
  const [jsonPreview, setJsonPreview] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [uploadedSpritesheets, setUploadedSpritesheets] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const pngRef = useRef(null)
  const jsonRef = useRef(null)

  const critterTypes = useCritterStore((state) => state.critterTypes)

  // Load uploaded spritesheets when panel opens
  useEffect(() => {
    if (isOpen && broadcasterLogin) {
      loadUploadedSpritesheets()
    }
  }, [isOpen, broadcasterLogin])

  const loadUploadedSpritesheets = async () => {
    setIsLoading(true)
    try {
      spritesheetApi.setBroadcasterLogin(broadcasterLogin)
      const list = await spritesheetApi.getCritterTypesWithSpritesheets()
      setUploadedSpritesheets(list)
    } catch (err) {
      console.error('Failed to load spritesheets:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handlePngChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      setPngFile(file)
      const reader = new FileReader()
      reader.onload = (e) => setPngPreview(e.target.result)
      reader.readAsDataURL(file)
    }
  }

  const handleJsonChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      setJsonFile(file)
      // Read JSON to show preview info
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result)
          const frameCount = Object.keys(data.frames || {}).length
          setJsonPreview(`${frameCount} frames`)
        } catch {
          setJsonPreview('Invalid JSON')
        }
      }
      reader.readAsText(file)
    }
  }

  const clearFiles = () => {
    setPngFile(null)
    setJsonFile(null)
    setPngPreview(null)
    setJsonPreview(null)
    if (pngRef.current) pngRef.current.value = ''
    if (jsonRef.current) jsonRef.current.value = ''
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    if (!selectedCritterType) {
      setError('Please select a critter type')
      return
    }

    if (!pngFile || !jsonFile) {
      setError('Both PNG spritesheet and JSON metadata are required')
      return
    }

    setIsSubmitting(true)

    try {
      spritesheetApi.setBroadcasterLogin(broadcasterLogin)
      await spritesheetApi.uploadSpritesheet(
        selectedCritterType,
        selectedAnimationState,
        pngFile,
        jsonFile
      )

      setSuccess(`Successfully uploaded ${selectedAnimationState} animation for ${selectedCritterType}`)
      clearFiles()
      await loadUploadedSpritesheets()
    } catch (err) {
      setError(err.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (critterType, animationState) => {
    if (!confirm(`Delete ${animationState} animation for ${critterType}?`)) {
      return
    }

    try {
      spritesheetApi.setBroadcasterLogin(broadcasterLogin)
      await spritesheetApi.deleteSpritesheet(critterType, animationState)
      await loadUploadedSpritesheets()
    } catch (err) {
      setError(err.message)
    }
  }

  if (!isOpen) return null

  return (
    <div className="spritesheet-overlay" onClick={onClose}>
      <div className="spritesheet-panel" onClick={(e) => e.stopPropagation()}>
        <div className="spritesheet-header">
          <h2>Upload Animated Spritesheets</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="spritesheet-content">
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <form onSubmit={handleSubmit} className="spritesheet-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="critterType">Critter Type</label>
                <select
                  id="critterType"
                  value={selectedCritterType}
                  onChange={(e) => setSelectedCritterType(e.target.value)}
                >
                  <option value="">Select a critter...</option>
                  {Object.entries(critterTypes).map(([key, type]) => (
                    <option key={key} value={key}>{type.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="animationState">Animation State</label>
                <select
                  id="animationState"
                  value={selectedAnimationState}
                  onChange={(e) => setSelectedAnimationState(e.target.value)}
                >
                  {Object.entries(ANIMATION_STATES).map(([key, value]) => (
                    <option key={key} value={value}>
                      {ANIMATION_STATE_LABELS[key]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="file-uploads">
              <div className="file-upload-group">
                <label>Spritesheet PNG</label>
                <div
                  className={`upload-zone ${pngPreview ? 'has-file' : ''}`}
                  onClick={() => pngRef.current?.click()}
                >
                  {pngPreview ? (
                    <>
                      <img src={pngPreview} alt="Spritesheet preview" className="spritesheet-preview" />
                      <button
                        type="button"
                        className="remove-file"
                        onClick={(e) => {
                          e.stopPropagation()
                          setPngFile(null)
                          setPngPreview(null)
                          if (pngRef.current) pngRef.current.value = ''
                        }}
                      >
                        &times;
                      </button>
                    </>
                  ) : (
                    <div className="upload-prompt">
                      <span className="upload-icon">🖼️</span>
                      <span>Click to upload PNG</span>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  ref={pngRef}
                  accept=".png"
                  onChange={handlePngChange}
                  hidden
                />
              </div>

              <div className="file-upload-group">
                <label>Metadata JSON</label>
                <div
                  className={`upload-zone ${jsonPreview ? 'has-file' : ''}`}
                  onClick={() => jsonRef.current?.click()}
                >
                  {jsonPreview ? (
                    <>
                      <div className="json-preview">
                        <span className="json-icon">📄</span>
                        <span>{jsonPreview}</span>
                      </div>
                      <button
                        type="button"
                        className="remove-file"
                        onClick={(e) => {
                          e.stopPropagation()
                          setJsonFile(null)
                          setJsonPreview(null)
                          if (jsonRef.current) jsonRef.current.value = ''
                        }}
                      >
                        &times;
                      </button>
                    </>
                  ) : (
                    <div className="upload-prompt">
                      <span className="upload-icon">📄</span>
                      <span>Click to upload JSON</span>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  ref={jsonRef}
                  accept=".json"
                  onChange={handleJsonChange}
                  hidden
                />
              </div>
            </div>

            <div className="form-hint">
              Upload TexturePacker-style spritesheet (PNG) and its metadata (JSON).
              Each animation state (idle, walk, eat, pet) needs its own spritesheet.
            </div>

            <button type="submit" className="submit-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Uploading...' : 'Upload Spritesheet'}
            </button>
          </form>

          {/* Uploaded Spritesheets List */}
          <div className="uploaded-section">
            <h3>Uploaded Spritesheets</h3>
            {isLoading ? (
              <p className="loading-text">Loading...</p>
            ) : uploadedSpritesheets.length === 0 ? (
              <p className="no-items-text">No spritesheets uploaded yet.</p>
            ) : (
              <div className="uploaded-list">
                {uploadedSpritesheets.map((item) => (
                  <div key={`${item.critter_type}-${item.animation_state}`} className="uploaded-item">
                    <div className="uploaded-info">
                      <span className="critter-type">{item.critter_type}</span>
                      <span className="animation-state">
                        {ANIMATION_STATE_LABELS[item.animation_state] || item.animation_state}
                      </span>
                    </div>
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(item.critter_type, item.animation_state)}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default UploadSpritesheetPanel
